import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  documentsApi,
  knowledgeBasesApi,
  type Document,
  type DocumentPayload,
  type KnowledgeBase,
  type KnowledgeBasePayload,
} from "./api";

const KEY = ["knowledge-bases"] as const;
const DOCS_KEY = ["documents"] as const;

export function useKnowledgeBases() {
  return useQuery({
    queryKey: [...KEY, "list"],
    queryFn: knowledgeBasesApi.list,
    staleTime: 30_000,
  });
}

export function useCreateKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: KnowledgeBasePayload) => knowledgeBasesApi.create(data),
    onSuccess: (created) => {
      qc.setQueryData<KnowledgeBase[]>([...KEY, "list"], (old) =>
        old ? [created, ...old] : [created],
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useUpdateKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<KnowledgeBasePayload>;
    }) => knowledgeBasesApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueryData<KnowledgeBase[]>([...KEY, "list"], (old) =>
        old?.map((kb) => (kb.id === updated.id ? { ...kb, ...updated } : kb)),
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useDeleteKnowledgeBase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => knowledgeBasesApi.remove(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<KnowledgeBase[]>([...KEY, "list"], (old) =>
        old?.filter((kb) => kb.id !== id),
      );
      qc.invalidateQueries({ queryKey: [...KEY, "list"] });
    },
  });
}

export function useKnowledgeBase(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, "detail", id],
    queryFn: () => knowledgeBasesApi.get(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useDocuments(kbId: string | undefined) {
  return useQuery({
    queryKey: [...DOCS_KEY, "list", kbId],
    queryFn: () => documentsApi.list(kbId!),
    enabled: !!kbId,
    staleTime: 15_000,
  });
}

function invalidateKbAndDocs(qc: ReturnType<typeof useQueryClient>, kbId: string) {
  qc.invalidateQueries({ queryKey: [...DOCS_KEY, "list", kbId] });
  qc.invalidateQueries({ queryKey: [...KEY, "detail", kbId] });
  qc.invalidateQueries({ queryKey: [...KEY, "list"] });
}

export function useCreateDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<DocumentPayload, "knowledge_base">) =>
      documentsApi.create({ ...data, knowledge_base: kbId }),
    onSuccess: () => invalidateKbAndDocs(qc, kbId),
  });
}

export function useUploadDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) => documentsApi.upload(formData),
    onSuccess: () => invalidateKbAndDocs(qc, kbId),
  });
}

export function useUpdateDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<DocumentPayload>;
    }) => documentsApi.update(id, data),
    onSuccess: (updated) => {
      qc.setQueryData<Document[]>(
        [...DOCS_KEY, "list", kbId],
        (old) => old?.map((d) => (d.id === updated.id ? { ...d, ...updated } : d)),
      );
      invalidateKbAndDocs(qc, kbId);
    },
  });
}

export function useDeleteDocument(kbId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => documentsApi.remove(id),
    onSuccess: (_data, id) => {
      qc.setQueryData<Document[]>(
        [...DOCS_KEY, "list", kbId],
        (old) => old?.filter((d) => d.id !== id),
      );
      invalidateKbAndDocs(qc, kbId);
    },
  });
}

export function useDocumentChunks(docId: string | null) {
  return useQuery({
    queryKey: [...DOCS_KEY, "chunks", docId],
    queryFn: () => documentsApi.chunks(docId!),
    enabled: !!docId,
    staleTime: 30_000,
  });
}

export function useSearchDocuments(kbId: string) {
  return useMutation({
    mutationFn: (query: string) =>
      documentsApi.search({ query, knowledge_base: kbId, top_k: 5 }),
  });
}
